import { Box, Stack, Typography } from "@mui/material";

interface BenefitAmount {
	is_present: boolean;
	is_visible_in_form: boolean;
	min_benefit: number;
	max_benefit: number;
	each_benefit_multiply_by: number;
	options: number[];
}

interface Props {
	benefitAmount?: BenefitAmount;
}

/* =========================
   Reusable Value Pill
========================= */
const ValuePill = ({ children }: { children: React.ReactNode }) => (
	<Box
		sx={{
			px: 1.35,
			py: 0.5,
			borderRadius: 1,
			backgroundColor: "#eef2ff", // light blue for benefits
			color: "#1e3a8a",
			fontSize: 14.5,
			fontWeight: 500,
			lineHeight: 1.35,
			whiteSpace: "nowrap",
			boxShadow: "0 1px 1px rgba(0,0,0,0.05)"
		}}
	>
		{children}
	</Box>
);

/* =========================
   Section Component
========================= */
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
	<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
		<Typography fontWeight={600} fontSize={13} color="text.secondary">
			{title}
		</Typography>
		{children}
	</Box>
);

/* =========================
   RuleCard Component
========================= */
const RuleCard = ({ title, value }: { title: string; value?: string | number }) => (
	<Box
		sx={{
			p: 1.25,
			borderRadius: 1,
			backgroundColor: "#f9fafb",
			minWidth: 150,
			flex: 1,
			boxShadow: "0 1px 2px rgba(0,0,0,0.06)"
		}}
	>
		<Typography fontWeight={600} fontSize={14.5} gutterBottom>
			{title}
		</Typography>
		<Typography fontSize={13} color="text.secondary">
			{value ?? "N/A"}
		</Typography>
	</Box>
);

/* =========================
   Main Component
========================= */
const BenefitAmountCard = ({ benefitAmount }: Props) => {
	const hasData = benefitAmount?.is_present;

	if (!hasData) {
		return (
			<Typography fontSize={14} color="text.secondary">
				N/A
			</Typography>
		);
	}

	return (
		<Box
			sx={{
				p: 2,
				borderRadius: 2,
				backgroundColor: "#fefefe",
				border: "1px solid #e5e7eb",
				display: "flex",
				flexDirection: "column",
				gap: 1.5
			}}
		>
			<Typography fontWeight={600} fontSize={15}>
				Benefit Amount
			</Typography>
			<Typography fontWeight={400} fontSize={12}>
				Specific Benefit
			</Typography>
			{/* Basic Details */}
			<Stack direction={{ xs: "column", md: "row" }} flexWrap="wrap" gap={1.5}>
				<RuleCard title="Minimum Benefit" value={benefitAmount.min_benefit} />
				<RuleCard title="Maximum Benefit" value={benefitAmount.max_benefit} />
				<RuleCard title="Multiply By" value={benefitAmount.each_benefit_multiply_by} />
			</Stack>

			{/* Options */}
			<Section title="Available Options">
				<Typography fontWeight={400} fontSize={12}>
					Benefit Bands
				</Typography>
				{benefitAmount.options?.length ? (
					<Stack direction="row" flexWrap="wrap" gap={0.75}>
						{benefitAmount.options.map((opt, idx) => (
							<ValuePill key={idx}>{opt.toLocaleString()}</ValuePill>
						))}
					</Stack>
				) : (
					<Typography fontSize={14} color="text.secondary">
						N/A
					</Typography>
				)}
			</Section>
		</Box>
	);
};

export default BenefitAmountCard;
